import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { useForm } from 'react-hook-form'

import {
    GrcTable,
    GrcTableBody,
    GrcTableContainer,
    GrcTableHead,
    GrcTableHeadRow,
    GrcTableRow,
    GrcTableCell
} from 'src/Common/ui/TableComponent'

import ToggleSwitch from 'src/Common/ui/ToggleSwitch'
import Racilist from 'src/Components/Governance/Components/raciLists'
import DatePickerInput from 'src/Common/ui/DatePickerInput'

import { getOneTimeCompliance } from 'src/Components/Governance/Store/ComplianceStore'
import { processOneTimeCompliance } from 'src/API/ComplianceAPI'
import { Button } from 'src/Common/ui/button'

/* ===================== RACI HELPERS ===================== */

const REQUIRED_RACI_IDS = [1, 2, 3, 4]

const createNoDataRaci = ({
    raciId,
    orgId,
    orgRegulationId,
    rootId
}) => ({
    department: 'nodata',
    departmentId: null,
    orgRegulationId,
    organizationId: orgId,
    orgId,
    profilePic: null,
    raciId,
    role: null,
    roleId: null,
    status: null,
    user: null,
    userId: null,
    rootId
})

const normalizeRaciMatrices = ({
    apiRaci = [],
    rootId,
    orgId,
    orgRegulationId
}) => {
    const map = new Map(
        apiRaci.map(r => [r.raciId, { ...r, rootId }])
    )

    return REQUIRED_RACI_IDS.map(raciId =>
        map.get(raciId) ||
        createNoDataRaci({
            raciId,
            orgId,
            orgRegulationId,
            rootId
        })
    )
}

/* ===================== COMPONENT ===================== */

const REGULATION_ID = { MCA: 1, IRDAI: 12 }

const OneTimeModal = ({ isOpen, onClose }) => {
    // grouped: { 1: [...], 12: [...] }
    const [groupedList, setGroupedList] = useState({ 1: [], 12: [] })
    const [activeTab, setActiveTab] = useState('MCA')
    const [updatedRaci, setUpdatedRaci] = useState(null)

    const {
        control,
        reset,
        formState: { errors }
    } = useForm({ mode: 'onChange' })

    const orgId = Number(localStorage.getItem('orgId'))
    const userId = Number(localStorage.getItem('userId'))

    /* ===================== FETCH ===================== */

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [mcaRes, irdaiRes] = await Promise.all([
                    getOneTimeCompliance(REGULATION_ID.MCA),
                    getOneTimeCompliance(REGULATION_ID.IRDAI),
                ])

                const normalize = (res, regId) =>
                    (res || [])
                        .filter(item => item.regulationId === regId)
                        .map(item => ({
                            ...item,
                            toggleStatus: item.toggleStatus ?? 2,
                            byWhen: item.byWhen ? new Date(item.byWhen) : null,
                            raciMatrices: normalizeRaciMatrices({
                                apiRaci: item.raciiMatrices || [],
                                rootId: item.id,
                                orgId,
                                orgRegulationId: item.orgRegulationId
                            })
                        }))

                setGroupedList({
                    [REGULATION_ID.MCA]: normalize(mcaRes, REGULATION_ID.MCA),
                    [REGULATION_ID.IRDAI]: normalize(irdaiRes, REGULATION_ID.IRDAI),
                })
            } catch (err) {
                console.error('Fetch error:', err)
            }
        }

        fetchData()
    }, [])

    const activeRegId = REGULATION_ID[activeTab]
    const seededList = groupedList[activeRegId] || []

    const updateActiveGroup = updater =>
        setGroupedList(prev => ({
            ...prev,
            [activeRegId]: updater(prev[activeRegId] || [])
        }))

    /* ===================== TOGGLE ===================== */

    const handleToggleChange = id => {
        updateActiveGroup(prev =>
            prev.map(item =>
                item.id === id
                    ? { ...item, toggleStatus: item.toggleStatus === 1 ? 2 : 1 }
                    : item
            )
        )
    }

    /* ===================== DATE ===================== */

    const handleDateChange = (id, value) => {
        updateActiveGroup(prev =>
            prev.map(item =>
                item.id === id ? { ...item, byWhen: value } : item
            )
        )
    }

    /* ===================== RACI UPDATE ===================== */

    useEffect(() => {
        if (!updatedRaci) return

        updateActiveGroup(prev =>
            prev.map(item =>
                item.id === updatedRaci.rootId
                    ? { ...item, raciMatrices: updatedRaci.data }
                    : item
            )
        )
    }, [updatedRaci])

    /* ===================== SAVE ===================== */

    const handleSave = async () => {
        const payload = {
            orgId,
            loginId: userId,
            regulationId: activeRegId,
            oneTimeCompliances: seededList
                .filter(item => item.toggleStatus === 1)
                .map(item => ({
                    id: item.id,
                    complianceId: item.complianceId,
                    regulationId: item.regulationId,
                    title: item.title,
                    description: item.description,
                    complainceFor: item.complainceFor,
                    category: item.category,
                    subCategory: item.subCategory,
                    action: item.action,
                    frequency: item.frequency,
                    timeline: item.timeline,
                    status: item.status,
                    toggleStatus: item.toggleStatus,
                    byWhen: item.byWhen ? item.byWhen.toISOString() : null,
                    raciiMatrices: item.raciMatrices.map(raci => ({
                        raciId: raci.raciId,
                        orgId: raci.orgId,
                        orgRegulationId: raci.orgRegulationId,
                        departmentId: raci.departmentId,
                        userId: raci.userId,
                        roleId: raci.roleId,
                        status: raci.status
                    }))
                }))
        }

        try {
            const res = await processOneTimeCompliance(payload)
            if (res?.status === 200 || res?.status === 201) {
                onClose()
            }
        } catch (err) {
            console.error('Save error:', err)
        }
    }

    if (!isOpen) return null

    /* ===================== UI ===================== */

    return createPortal(
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
            <div className="bg-white w-[90%] md:w-[80%] max-h-[85vh] rounded-lg shadow-2xl flex flex-col">

                {/* HEADER */}
                <div className="flex justify-between !bg-[white] items-center px-6 py-4 border-b">
                    <h2 className="text-xl text-[black] font-semibold">One-Time Compliance</h2>
                    <button onClick={onClose}>
                        <X size={22} />
                    </button>
                </div>

                {/* TABS */}
                <div className="flex border-b bg-[white]">
                    {['MCA', 'IRDAI'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`flex-1 py-2 text-[black] font-medium ${activeTab === tab
                                ? 'border-b-2 border-teal-600 text-teal-700'
                                : 'text-gray-500'
                                }`}
                        >
                            {tab}
                        </button>
                    ))}
                </div>

                {/* TABLE */}
                <div className="flex-1 overflow-auto p-4 bg-[white]">
                    <GrcTableContainer className="border rounded-md">
                        <GrcTable>
                            <GrcTableHead>
                                <GrcTableHeadRow className="bg-teal-50">
                                    <GrcTableCell>#</GrcTableCell>
                                    <GrcTableCell>Yes / No</GrcTableCell>
                                    <GrcTableCell>Compliance Title</GrcTableCell>
                                    <GrcTableCell>Description</GrcTableCell>
                                    <GrcTableCell>Form</GrcTableCell>
                                </GrcTableHeadRow>
                            </GrcTableHead>

                            <GrcTableBody>
                                {seededList.map((item, index) => (
                                    <React.Fragment key={item.id}>
                                        <GrcTableRow>
                                            <GrcTableCell>{index + 1}</GrcTableCell>
                                            <GrcTableCell>
                                                <ToggleSwitch
                                                    isChecked={item.toggleStatus === 1}
                                                    onChange={() => handleToggleChange(item.id)}
                                                />
                                            </GrcTableCell>
                                            <GrcTableCell>{item.title}</GrcTableCell>
                                            <GrcTableCell>{item.description}</GrcTableCell>
                                            <GrcTableCell>{item.form}</GrcTableCell>
                                        </GrcTableRow>

                                        {item.toggleStatus === 1 && (
                                            <GrcTableRow>
                                                <GrcTableCell colSpan={7} className="p-4 bg-gray-50">
                                                    <DatePickerInput
                                                        name={`byWhen-${item.id}`}
                                                        label="By when"
                                                        value={item.byWhen}
                                                        control={control}
                                                        errors={errors}
                                                        onValueChange={val =>
                                                            handleDateChange(item.id, val)
                                                        }
                                                    />

                                                    <p className="font-semibold mt-4 mb-2">RACI</p>

                                                    <Racilist
                                                        tempRaciMatrix={item.raciMatrices}
                                                        raciiMatrices={item.raciMatrices}
                                                        setRaciiMatrices={data =>
                                                            setUpdatedRaci({
                                                                rootId: item.id,
                                                                data
                                                            })
                                                        }
                                                        getState={{ id: item.id }}
                                                        control={control}
                                                        errors={errors}
                                                        reset={reset}
                                                    />
                                                </GrcTableCell>
                                            </GrcTableRow>
                                        )}
                                    </React.Fragment>
                                ))}
                            </GrcTableBody>
                        </GrcTable>
                    </GrcTableContainer>
                </div>


                <div className="flex justify-end bg-[white] gap-3 px-6 py-4 border-t bg-gray-50">
                    <Button onClick={onClose}
                        style={{ color: 'hsl(var(--destructive))', background: 'white', borderColor: 'hsl(var(--destructive))' }}

                        className="px-4 py-2 border text-[black] rounded-md">
                        Cancel
                    </Button>
                    {/* <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-teal-600 text-[black] rounded-md"
                    >
                        Save
                    </button> */}
                    <Button onClick={handleSave} >Save</Button>
                </div>
            </div>
        </div>,
        document.getElementById('portal-root') || document.body
    )
}

export default OneTimeModal
